import {
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SOIL_INPUT,
  TASKS_GRID_MOA_CAPTION_PREFIX,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import {
  IInputStep,
  IInputStepSoilInput,
  InputStepType,
} from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import {
  computeTaskNutritionPerHa,
  NUTRIENT_DISPLAY_ORDER,
  NUTRIENT_SHORT_LABELS,
} from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';

import { getTaskSteps } from './stepCalculations';

// Same rounding as the Nutrition lens, so the figures read identically.
export const formatNutrientRate = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

export interface NutrientTallyItem {
  symbol: string;
  rate: number;
}

/**
 * The nutrients a task applies (kg/ha), in the Nutrition lens's order and on
 * its basis (computeTaskNutritionPerHa). Empty when it applies none.
 */
export const buildTaskNutrientTally = (
  task: IInputTask,
  planInput: IInput,
  gUnits: GUnit[]
): NutrientTallyItem[] => {
  const perHa = computeTaskNutritionPerHa(task, planInput, gUnits);
  return NUTRIENT_DISPLAY_ORDER.filter((key) => (perHa[key] ?? 0) > 0).map(
    (key) => ({ symbol: NUTRIENT_SHORT_LABELS[key], rate: perHa[key] ?? 0 })
  );
};

const getStepMoaGroups = (step: IInputStep): string[] =>
  'activeConstituents' in step
    ? (step.activeConstituents ?? [])
        .map((constituent) => constituent.snapMoaGroup?.trim() ?? '')
        .filter(Boolean)
    : [];

/**
 * The MoA groups of a task's products, each once, in the order they first
 * appear. Products without MoA data are skipped.
 */
export const buildTaskMoaGroups = (task: IInputTask): string[] =>
  Array.from(new Set(getTaskSteps(task).flatMap(getStepMoaGroups)));

/**
 * A product's own analysis under its name, shown only in the focused views
 * (OP-2534): nutrient percentages with the Soil Input filter on, e.g.
 * "N 46% · S 5%", and MoA groups with Plant Protection on, e.g. "MoA 3, 11".
 * Null in other views or when the product has none.
 */
export const getStepFilterDetail = (
  step: IInputStep,
  categoryKey: string
): string | null => {
  if (
    categoryKey === SUMMARY_ENTITY_SOIL_INPUT &&
    step.stepType === InputStepType.SoilInput
  ) {
    const soilInput = step as IInputStepSoilInput;
    const parts = NUTRIENT_DISPLAY_ORDER.filter(
      (key) => (soilInput[key] ?? 0) > 0
    ).map((key) => `${NUTRIENT_SHORT_LABELS[key]} ${soilInput[key]}%`);
    return parts.length > 0 ? parts.join(' · ') : null;
  }
  if (categoryKey === SUMMARY_ENTITY_PLANT_PROTECTION) {
    const groups = Array.from(new Set(getStepMoaGroups(step)));
    return groups.length > 0
      ? `${TASKS_GRID_MOA_CAPTION_PREFIX} ${groups.join(', ')}`
      : null;
  }
  return null;
};
